import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator';
import { Button } from '@akin-home/buttons/native';
import { Text } from '@akin-home/text/native';
import { colors, spacing } from '@akin-home/theme';
import { showErrorToast } from '@akin-home/toast/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';
import { KnownRobotCard } from '../../components/KnownRobotsBar/KnownRobotCard';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { RobotIdentityDetails } from '../components/RobotIdentityDetails';
import { RobotIdentityRow } from '../components/RobotIdentityRow';
import { useGetRobotIdentities } from '../hooks/queries';
import { RobotIdentity } from '../types';

export const RobotIdentitiesScreen = (
  props: RootStackScreenProps<'RobotIdentities'>
) => {
  const {
    navigation: { goBack, navigate },
    route: {
      params: { robotId },
    },
  } = props;

  useUpdateScreenChatContext();
  // useEffect(() => {
  //   sendBackgroundMessage(intent_labels.open_create_checklist_screen);
  // }, []);

  const robot = useRobotStateStore((state) => state.robotDevices[robotId]);

  if (!robot) {
    showErrorToast({
      text1: 'Robot not found.',
    });
    goBack();
  }

  const { data: identities } = useGetRobotIdentities(robot, {
    select: (data) => data.sort(),
  });

  const { primaryUsers, otherIdentities } = useMemo(() => {
    const primaryUsers: RobotIdentity[] = [];
    const otherIdentities: RobotIdentity[] = [];
    identities?.forEach((ident) => {
      if (ident.is_primary_user) {
        primaryUsers.push(ident);
      } else {
        otherIdentities.push(ident);
      }
    });

    return { primaryUsers, otherIdentities };
  }, [identities]);

  const [selectedIdentity, setSelectedIdentity] = useState<
    string | undefined
  >();

  return (
    <>
      <Screen>
        <Header>
          <ScreenBackButton onPress={goBack} />
          <ScreenTitle>Robot Identities</ScreenTitle>

          <View style={styles.robotCardContainer}>
            {robot ? (
              <KnownRobotCard robot={robot} />
            ) : (
              <>
                <FontAwesomeIcon
                  icon={'warning'}
                  size={30}
                  style={{ height: '100%', width: '100%' }}
                  color={colors.yellow[700]}
                />
                <Text
                  style={{
                    textAlign: 'center',
                    textAlignVertical: 'center',
                    fontSize: 22,
                    color: colors.yellow[700],
                  }}
                >
                  Robot Not Available
                </Text>
              </>
            )}
          </View>
        </Header>

        <View style={styles.screen}>
          <View style={styles.section}>
            <ScrollView
              contentContainerStyle={{
                justifyContent: 'flex-start',
                gap: spacing[2],
                paddingBottom: spacing[8] * 1.6,
              }}
            >
              <Text variant="h3" weight="semibold">
                Primary User
              </Text>
              {primaryUsers.map((identity) => (
                <RobotIdentityRow
                  key={identity.uuid}
                  identity={identity}
                  onPress={() => setSelectedIdentity(identity.uuid)}
                  selected={selectedIdentity === identity.uuid}
                />
              ))}

              <Text variant="h3" weight="semibold">
                Other Identities
              </Text>
              {otherIdentities.map((identity) => (
                <RobotIdentityRow
                  key={identity.uuid}
                  identity={identity}
                  onPress={() => setSelectedIdentity(identity.uuid)}
                  selected={selectedIdentity === identity.uuid}
                />
              ))}
            </ScrollView>

            <View style={styles.floatingButtonContainer}>
              <Button
                size="large"
                icon="plus"
                onPress={() => navigate('CreateRobotIdentity', { robotId })}
              >
                Create New Identity
              </Button>
            </View>
          </View>

          <View style={styles.verticalSpacer}></View>

          <View style={styles.section}>
            <RobotIdentityDetails
              robotId={robotId}
              identity_id={selectedIdentity}
              onClose={() => setSelectedIdentity(undefined)}
            />
          </View>
        </View>
      </Screen>
      <DeveloperScreenInfo />
    </>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
  padding-bottom: 10px;
`;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: 'white',
    margin: spacing[5],
    marginTop: 0,
    elevation: 3,
    borderRadius: 10,
  },
  robotCardContainer: {
    marginLeft: 'auto',
    flexDirection: 'row',
    gap: spacing[2],
  },
  section: {
    flex: 1,
    margin: spacing[4],
  },
  sectionHeader: {
    fontSize: 25,
  },
  horizontalSpacer: {
    backgroundColor: '#dddddd',
    height: 1,
    borderRadius: 10,
  },
  verticalSpacer: {
    backgroundColor: '#dddddd',
    width: 1,
    marginVertical: spacing[3],
    borderRadius: 10,
  },
  floatingButtonContainer: {
    position: 'absolute',
    bottom: 10,
    // This is to center the button. unfortunately, this may reduce responsiveness. So consider replacing with an onlayout based approach.
    left: '25%',
    right: '25%',
    flexDirection: 'row',
    justifyContent: 'center',
  },
});
